import { Team } from "@/types";
import { differenceInCalendarDays, startOfDay, subDays } from "date-fns";

/**
 * Derive a safe, human-friendly username from an email address.
 *
 * Uses ONLY the local part (before "@") so a user's full email address is
 * never exposed as their public display name. Strips characters outside a
 * safe set, trims leading/trailing separators and caps the length. Returns
 * an empty string when nothing usable remains, so the caller can apply its
 * own final fallback (e.g. a translated "Player" label).
 */
export function usernameFromEmail(email: string | null | undefined): string {
  if (!email) return "";
  const localPart = email.split("@")[0] ?? "";
  return localPart
    .replace(/[^a-zA-Z0-9._-]+/g, "")
    .replace(/^[._-]+|[._-]+$/g, "")
    .slice(0, 30);
}

/**
 * Format milliseconds as HH:MM:SS countdown string.
 */
export function formatCountdown(ms: number): string {
  if (ms <= 0) return "00:00:00";
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Determines if a team is currently on vacation based on their vacation periods.
 * Checks if the current date falls within any active vacation period.
 *
 * @param team - The team to check
 * @returns true if team is currently on vacation, false otherwise
 */
export function teamIsOnVacation(team: Team): boolean {
  if (!team.vacations || team.vacations.length === 0) {
    return false;
  }

  const today = startOfDay(new Date());
  return team.vacations.some((vacation) => {
    const start = startOfDay(new Date(vacation.start));
    const end = startOfDay(new Date(vacation.end));
    return today >= start && today <= end;
  });
}

/**
 * Calculates the number of days remaining in the current vacation period.
 * Returns the days until the active vacation ends.
 *
 * @param team - The team to check
 * @returns Number of days remaining in vacation, or 0 if not on vacation
 */
export function getVacationDaysRemaining(team: Team): number {
  if (!team.vacations || team.vacations.length === 0) {
    return 0;
  }

  const today = startOfDay(new Date());
  const activeVacation = team.vacations.find((vacation) => {
    const start = startOfDay(new Date(vacation.start));
    const end = startOfDay(new Date(vacation.end));
    return today >= start && today <= end;
  });

  if (!activeVacation) {
    return 0;
  }

  const end = startOfDay(new Date(activeVacation.end));
  const diffDays = differenceInCalendarDays(end, today) + 1;
  return Math.max(0, diffDays);
}

/**
 * Calculates how far the team is through its current vacation, as a fraction
 * from 0 (just started) to 1 (about to end). Used to render an "elapsed"
 * progress bar on the team card — the bar fills up as the break goes on.
 *
 * Time-based (not whole-day) so the fill advances smoothly within a day, while
 * the accompanying "X days remaining" label stays day-granular.
 *
 * @param team - The team to check
 * @returns Elapsed fraction in [0, 1], or 0 if the team is not on vacation
 */
export function getVacationProgress(team: Team): number {
  if (!team.vacations || team.vacations.length === 0) {
    return 0;
  }

  const today = startOfDay(new Date());
  const activeVacation = team.vacations.find((vacation) => {
    const start = startOfDay(new Date(vacation.start));
    const end = startOfDay(new Date(vacation.end));
    return today >= start && today <= end;
  });

  if (!activeVacation) {
    return 0;
  }

  const start = new Date(activeVacation.start).getTime();
  const end = new Date(activeVacation.end).getTime();
  if (end <= start) {
    return 0;
  }

  const fraction = (Date.now() - start) / (end - start);
  return Math.min(1, Math.max(0, fraction));
}

/**
 * Gets the number of days until the team's next upcoming vacation starts.
 * Returns null if there's no upcoming vacation within the specified threshold.
 *
 * @param team - The team to check
 * @param thresholdDays - Only return value if vacation starts within this many days
 * @returns Number of days until vacation starts, or null if no vacation within threshold
 */
export function getUpcomingVacationDays(
  team: Team,
  thresholdDays: number,
): number | null {
  if (!team.vacations || team.vacations.length === 0) {
    return null;
  }

  const now = new Date();
  // Find the next upcoming vacation (not currently active)
  const upcomingVacation = team.vacations
    .filter((vacation) => {
      const start = new Date(vacation.start);
      return start > now;
    })
    .sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
    )[0];

  if (!upcomingVacation) {
    return null;
  }

  const start = new Date(upcomingVacation.start);
  const diffMs = start.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  // Return days if within threshold, otherwise null
  return diffDays <= thresholdDays ? diffDays : null;
}

/**
 * Calculates remaining vacation periods for a team.
 * Each vacation period is 7 days. This function calculates how many periods
 * have been used and subtracts from the tournament maximum.
 *
 * @param team - The team to check
 * @param maxVacationPeriods - Maximum vacation periods allowed by tournament
 * @returns Number of remaining vacation periods
 */
export function getRemainingVacationPeriods(
  team: Team,
  maxVacationPeriods: number,
): number {
  if (!team.vacations || team.vacations.length === 0) {
    return maxVacationPeriods;
  }

  const MS_PER_PERIOD = 7 * 24 * 60 * 60 * 1000;
  const usedPeriods = team.vacations.reduce((total, vacation) => {
    const start = new Date(vacation.start);
    const end = new Date(vacation.end);
    // Ms-based (not calendar-days) so UTC/local TZ mismatch doesn't inflate count.
    const periods = Math.ceil((end.getTime() - start.getTime()) / MS_PER_PERIOD);
    return total + Math.max(1, periods);
  }, 0);

  return Math.max(0, maxVacationPeriods - usedPeriods);
}

/**
 * Checks whether a proposed vacation range conflicts with any existing vacation
 * period, given the tournament's vacation protection window.
 *
 * Returns:
 *  - "overlap": the proposed range overlaps an existing vacation
 *  - "bufferRequired": there is a gap, but it is shorter than 2×protection days
 *    (back-to-back is allowed; otherwise the unprotected playtime between
 *    periods must be at least `vacationProtectionDays` — this prevents stacking
 *    two full protection+vacation blocks into one continuous safe stretch)
 *  - null: no conflict
 */
export type VacationSchedulingConflict = "overlap" | "bufferRequired";

export function getVacationSchedulingConflict(
  proposedStart: Date,
  proposedEnd: Date,
  existingVacations: Array<{ start: Date | string; end: Date | string }>,
  vacationProtectionDays: number,
): VacationSchedulingConflict | null {
  if (!existingVacations || existingVacations.length === 0) return null;

  // Compare raw timestamps. Vacation dates are stored as UTC instants that
  // encode local-time boundaries (start at 00:00 local, end at 23:59:59.999
  // local), so adjacent periods are separated by 1 ms. Re-flooring with
  // startOfDay would use the JS runtime's TZ — on a UTC server that collapses
  // adjacent periods onto the same UTC day and produces a false overlap.
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const pStart = proposedStart.getTime();
  const pEnd = proposedEnd.getTime();
  const minGapDays = vacationProtectionDays * 2;

  for (const vacation of existingVacations) {
    const eStart = new Date(vacation.start).getTime();
    const eEnd = new Date(vacation.end).getTime();

    if (pStart <= eEnd && pEnd >= eStart) {
      return "overlap";
    }

    const gapDays =
      pEnd < eStart
        ? Math.floor((eStart - pEnd) / MS_PER_DAY)
        : Math.floor((pStart - eEnd) / MS_PER_DAY);

    if (gapDays > 0 && gapDays < minGapDays) {
      return "bufferRequired";
    }
  }

  return null;
}

/**
 * Computes the vacation guard ("protection") window start for a single vacation,
 * i.e. the moment from which the team can no longer be challenged ahead of the
 * vacation. Returns `null` when no separate guard window effectively exists, so
 * callers can hide the misleading "protected starting from…" note.
 *
 * The guard normally begins `protectionDays` before the vacation start. But when
 * the team is already on another vacation throughout that window (back-to-back
 * vacations — allowed by `getVacationSchedulingConflict` with a zero gap), the
 * guard adds no extra unavailable time: the team is continuously unavailable
 * straight from the earlier vacation. Advertising a guard start that falls
 * inside the preceding vacation is misleading, so we return `null` instead.
 *
 * Because scheduling only permits adjacent vacations (gap 0) or a gap of at
 * least `2 × protectionDays`, a guard window can overlap a preceding vacation
 * only in the back-to-back case — there is no partial-overlap case to clamp.
 *
 * @param vacation - The vacation whose guard window to compute
 * @param allVacations - All of the team's vacations (used to detect a preceding one)
 * @param protectionDays - Tournament vacation protection days
 * @returns The guard start date, or `null` when the guard is redundant/disabled
 */
export function getVacationGuardStart(
  vacation: { start: Date | string; end: Date | string },
  allVacations: Array<{ start: Date | string; end: Date | string }>,
  protectionDays: number,
): Date | null {
  if (protectionDays <= 0) return null;

  const start = new Date(vacation.start).getTime();
  const guardStart = subDays(new Date(start), protectionDays);

  // If any earlier vacation runs into this vacation's guard window, the team is
  // already unavailable for the whole window — there is no separate guard.
  const absorbedByPreceding = allVacations.some((other) => {
    const otherStart = new Date(other.start).getTime();
    const otherEnd = new Date(other.end).getTime();
    return otherStart < start && otherEnd >= guardStart.getTime();
  });

  return absorbedByPreceding ? null : guardStart;
}

export interface VacationDisplayRange {
  /** Key for rendering — the id of the earliest source vacation. */
  id: string;
  start: Date;
  end: Date;
  /** Source vacations composing this range, earliest first (length >= 1). */
  sources: Array<{ id: string; start: Date | string; end: Date | string }>;
}

/**
 * Merges back-to-back vacations into single display ranges. Adjacent vacations
 * are stored ~1ms apart (one ends at 23:59:59.999 local, the next starts at
 * 00:00 local), representing one continuous unavailable stretch. The UI should
 * present that as a single date range rather than several rows.
 *
 * Any gap under a day is treated as continuous. Scheduling only permits a zero
 * gap or a gap of at least `2 × protectionDays`, so there is no ambiguous
 * middle ground — a sub-day gap is always a true back-to-back pair.
 */
export function mergeBackToBackVacations(
  vacations: Array<{ id: string; start: Date | string; end: Date | string }>,
): VacationDisplayRange[] {
  if (!vacations || vacations.length === 0) return [];

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const sorted = [...vacations].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
  );

  const ranges: VacationDisplayRange[] = [];
  for (const vacation of sorted) {
    const start = new Date(vacation.start);
    const end = new Date(vacation.end);
    const current = ranges[ranges.length - 1];

    if (current && start.getTime() - current.end.getTime() < MS_PER_DAY) {
      if (end.getTime() > current.end.getTime()) current.end = end;
      current.sources.push(vacation);
    } else {
      ranges.push({ id: vacation.id, start, end, sources: [vacation] });
    }
  }

  return ranges;
}

/**
 * Plays the chicken sound effect (for declining braveheart challenges).
 * Uses the Audio API to play the sound, with error handling for browsers
 * that don't support autoplay.
 */
export function playChickenSound(): void {
  try {
    const audio = new Audio("/assets/chicken-noise.mp3");
    audio.volume = 0.5;
    audio.play().catch((error) => {
      // Silently fail if audio can't play (e.g., autoplay policy)
      console.debug("Could not play chicken sound:", error);
    });
  } catch (error) {
    console.debug("Failed to create audio:", error);
  }
}
